import React, { useEffect, useState, useRef } from 'react'
import { Modal, Progress, Button } from 'antd'
import styled from 'styled-components'
import { pollAssignmentStatus, subscribeAssignmentSSE, getAssignmentStatus } from '../../services/assignmentActivityAPI'

/* ───────── Theme tokens (match ReviewAssessmentsList) ───────── */
const T = {
  primary: '#3249D7',
  textPrimary: '#242E39',
  textSecondary: '#637487',
  border: '#EBECEE',
  green: '#4cb187',
  red: '#E5484D',
  grey95: '#F2F3F5',
}

// Above this size we promise an emailed summary (matches backend ASSIGNMENT_SUMMARY_MIN).
const SUMMARY_THRESHOLD = 50

const Body = styled.div`padding: 4px 4px 0;`
const Title = styled.div`
  font-size: 16px; font-weight: 700; color: ${T.textPrimary}; margin-bottom: 6px;
`
const Sub = styled.p`
  font-size: 13px; line-height: 1.6; color: ${T.textSecondary}; margin: 0 0 18px;
`
const Counts = styled.div`
  display: flex; gap: 18px; margin: 14px 0 4px;
`
const Stat = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  .n { font-size: 20px; font-weight: 700; color: ${(p) => p.$color || T.textPrimary}; }
  .l { font-size: 11px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; color: ${T.textSecondary}; }
`
const SecondaryLine = styled.div`
  font-size: 12px; color: ${T.textSecondary}; margin-top: 10px; min-height: 18px;
`
const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 22px; padding-top: 16px; border-top: 1px solid ${T.border};
`
const LinkBtn = styled.button`
  background: none; border: none; color: ${T.primary}; font-weight: 600; font-size: 13px;
  cursor: pointer; padding: 0;
`
const ContinueBtn = styled(Button)`
  background: ${T.primary}; border-color: ${T.primary}; font-weight: 600;
  &:hover, &:focus { background: ${T.primary}; border-color: ${T.primary}; opacity: .92; }
`

const TERMINAL = ['completed', 'completed_with_errors', 'failed']

/**
 * Submit-and-go popup shown right after an async assign returns { jobId }.
 * Live progress = students ASSIGNED (the gate before notification). Continue is
 * non-blocking — the job keeps running server-side.
 *
 * Props: open, jobId, total, assessmentName, onContinue(), onClose(), onViewActivity(jobId)
 */
const AssignmentProgressModal = ({ open, jobId, total = 0, assessmentName, onContinue, onClose, onViewActivity }) => {
  const [p, setP] = useState({ assigned: 0, provisioned: 0, failed: 0, new: 0, total, state: 'queued' })
  const stopRef = useRef(null)
  const esRef = useRef(null)

  // SSE-first for live updates; fall back to polling if the stream errors.
  // Always loads via whichever arrives — table/job rows are the source of truth.
  useEffect(() => {
    if (!open || !jobId) return undefined
    const TERMINAL = ['completed', 'completed_with_errors', 'failed']
    let cancelled = false
    const apply = (data) => { if (data && !cancelled) setP((prev) => ({ ...prev, ...data })) }

    let pollingStarted = false
    const startPolling = () => {
      if (pollingStarted || cancelled) return
      pollingStarted = true
      stopRef.current = pollAssignmentStatus(jobId, apply)
    }

    // Hydrate current state immediately on open. A fast/small job can reach a
    // terminal state BEFORE this SSE subscription lands; SSE has no backlog/replay
    // and only sends CONNECTED on connect, so without this snapshot the modal would
    // stay stuck at 0/total even though the job already finished (job rows are the
    // source of truth). Best-effort — SSE/poll still drive live deltas.
    getAssignmentStatus(jobId)
      .then((res) => {
        const data = res?.data?.data
        if (!data) return
        apply(data)
        if (TERMINAL.includes(data.state) && esRef.current) { esRef.current.close() }
      })
      .catch(() => { /* snapshot best-effort; SSE/poll still cover live updates */ })

    try {
      const es = subscribeAssignmentSSE(jobId)
      esRef.current = es
      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data)
          if (data.type === 'CONNECTED') return
          apply(data)
          if (TERMINAL.includes(data.state)) { es.close() }
        } catch (e) { /* ignore malformed frame */ }
      }
      es.onerror = () => { es.close(); startPolling() } // stream dropped → poll
    } catch (e) {
      startPolling()
    }

    return () => {
      cancelled = true
      if (esRef.current) esRef.current.close()
      if (stopRef.current) stopRef.current()
    }
  }, [open, jobId])

  const denom = p.total || total || 0
  const pct = denom ? Math.round((p.assigned / denom) * 100) : 0
  const isTerminal = TERMINAL.includes(p.state)
  const hasFailures = p.failed > 0
  const largeBatch = denom >= SUMMARY_THRESHOLD

  // Set preparation is a distinct phase that runs BEFORE any student is touched:
  // a free-text topic or a non-default listening accent has no pool set to pick,
  // so it needs an LLM + text-to-speech round-trip first. Every student counter is
  // legitimately 0 while that happens, so show the generation's own progress
  // instead of a stalled "0/N assigned" that reads like nothing is happening.
  const prepare = p.prepare
  const isPreparing = !isTerminal && p.state === 'preparing' && Boolean(prepare)
  const preparePct = prepare?.total ? Math.round((prepare.ready / prepare.total) * 100) : 0
  const prepareNoun = `question set${prepare?.total > 1 ? 's' : ''}`

  let strokeColor = T.primary
  if (isTerminal) strokeColor = hasFailures ? T.red : T.green

  return (
    <Modal open={open} footer={null} closable={false} maskClosable={false} width={460} centered onCancel={onClose}>
      <Body>
        <Title>
          {isPreparing
            ? (prepare.isGenerating ? `Generating ${prepareNoun}` : `Preparing ${prepareNoun}`)
            : isTerminal
              ? (hasFailures ? 'Assignment finished with some failures' : 'Assignment complete')
              : 'Assigning is in progress'}
          {assessmentName ? <span style={{ color: T.textSecondary, fontWeight: 600 }}> · {assessmentName}</span> : null}
        </Title>
        <Sub>
          {isPreparing
            ? `Writing the questions and narration audio${prepare.accent ? ` in ${prepare.accent}` : ''}. This is the slow part — students are invited automatically once it finishes, so you can wait here or continue.`
            : isTerminal
              ? (hasFailures
                  ? `${p.assigned} of ${denom} students were assigned. ${p.failed} need attention.`
                  : `All ${denom} students have been assigned the assessment.`)
              : (largeBatch
                  ? "This can take some time — we'll email you a summary once it's done. You can wait here or continue; it keeps running in the background."
                  : 'This will only take a moment. You can wait here or continue — it keeps running in the background.')}
        </Sub>

        <Progress percent={isPreparing ? preparePct : pct} strokeColor={strokeColor} showInfo={false} />

        <Counts>
          {isPreparing
            ? <Stat $color={T.textPrimary}><span className="n">{prepare.ready}/{prepare.total}</span><span className="l">Sets ready</span></Stat>
            : <Stat $color={T.textPrimary}><span className="n">{p.assigned}/{denom}</span><span className="l">Assigned</span></Stat>}
          {!isPreparing && hasFailures && <Stat $color={T.red}><span className="n">{p.failed}</span><span className="l">Failed</span></Stat>}
        </Counts>

        {/* Secondary line: the topic being generated, else account creation for new
            students (both explain why early progress looks slow). */}
        <SecondaryLine>
          {isPreparing
            ? (prepare.topic ? `Topic: ${prepare.topic}` : '')
            : !isTerminal && p.new > 0 && p.provisioned < p.new
              ? `Creating ${p.provisioned}/${p.new} new student accounts…`
              : ''}
        </SecondaryLine>

        <Footer>
          {isTerminal && hasFailures
            ? <LinkBtn onClick={() => onViewActivity && onViewActivity(jobId)}>View failures &amp; retry →</LinkBtn>
            : <span />}
          <ContinueBtn type="primary" onClick={onContinue}>
            {isTerminal ? 'Done' : 'Continue'}
          </ContinueBtn>
        </Footer>
      </Body>
    </Modal>
  )
}

export default AssignmentProgressModal
